<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="() => { error.title = ''; error.body = ''; }"
  />

  <div v-else>
    <div class="flex flex-row">
      <div class="flex flex-col">
        <action-button :title="t('print')" faIcon="print" @click="print" />
      </div>

      <template v-if="$ctx.getReceiptSize() == 'a4' || $ctx.getReceiptSize() == 'a5'">
        <InventoryReceivingReceiptA4
          :inventoryReceiving="inventoryReceiving"
          :transactions="transactions"
        />
      </template>

      <template v-if="$ctx.getReceiptSize() == 'receipt'">
        <InventoryReceivingReceiptSmall
          :inventoryReceiving="inventoryReceiving"
          :transactions="transactions"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import AlertBadge from "@/components/AlertBadge.vue";
import ActionButton from "@/components/ActionButton.vue";
import InventoryReceiving from "@/models/inventory/InventoryReceiving";
import { Transaction } from "@/models/accounting/Transaction";
import InventoryReceivingReceiptA4 from "./InventoryReceivingReceiptA4.vue";
import InventoryReceivingReceiptSmall from "./InventoryReceivingReceiptSmall.vue";
import Dinero from "dinero.js";

export default defineComponent({
  name: 'InventoryReceivingReceipt',

  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton,
    InventoryReceivingReceiptA4,
    InventoryReceivingReceiptSmall
  },

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          print: "Print Receipt"
        },
        ar: {
          print: "اطبع الإيصال"
        }
      }
    });

    return { t };
  },

  data() {
    return {
      inventoryReceiving: new InventoryReceiving(),
      transactions: new Array<Transaction>(),
      loading: true,
      error: {
        title: "",
        body: ""
      }
    };
  },

  async mounted() {
    try {
      const id = this.$route.params.id as string;
      const transactionID = this.$route.query.transactionID as string | undefined;

      // Load inventory receiving
      const receivingResp = await this.$http.get<InventoryReceiving>(`${InventoryReceiving.ENDPOINT}/${id}`);
      this.inventoryReceiving = InventoryReceiving.from(receivingResp);

      // Initialize totalCostDinero if not set - calculate from items
      if (!this.inventoryReceiving.totalCostDinero) {
        let totalAmount = 0;
        if (this.inventoryReceiving.totalCostAmount !== undefined) {
          totalAmount = this.inventoryReceiving.totalCostAmount;
        } else if (this.inventoryReceiving.inventoryReceivingItems?.length > 0) {
          // Calculate from items if totalCostAmount is not available
          for (const item of this.inventoryReceiving.inventoryReceivingItems) {
            if (item.costDinero) {
              totalAmount += item.costDinero.getAmount() * item.quantity;
            }
          }
        }

        this.inventoryReceiving.totalCostDinero = Dinero({
          amount: totalAmount,
          currency: 'IQD',
          precision: 0
        });
      }

      // Load transactions for this inventory receiving (from vendor bill if exists)
      if (this.inventoryReceiving.vendorBill?.transactions && this.inventoryReceiving.vendorBill.transactions.length > 0) {
        let allTransactions = this.inventoryReceiving.vendorBill.transactions.map(t => Transaction.from(t)).filter(t => t.isReceipt);

        // If transactionID is provided, filter to show only that transaction
        if (transactionID) {
          this.transactions = allTransactions.filter(t => t.id === transactionID);
        } else {
          this.transactions = allTransactions;
        }
      }
    } catch (error) {
      this.error.title = error.title || 'Error loading inventory receiving';
      this.error.body = error.body || '';
    } finally {
      this.loading = false;
    }
  },

  methods: {
    print() {
      window.print();
    }
  }
});
</script>

<style>
</style>
